import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  archiveCourse,
  createModule,
  deleteCourse,
  deleteModule,
  getCourse,
  getModule,
  publishCourse,
  updateCourse,
  type CourseDetail,
  type ModuleDetail,
  type ModuleListItem,
} from "../api/courses";
import { ApiError } from "../api/client";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { AssignmentsPanel } from "../components/AssignmentsPanel";
import { CoverUploader } from "../components/CoverUploader";
import { fileUrl } from "../api/files";

function canManageAssignments(roles: string[]): boolean {
  return roles.some(
    (r) =>
      r === "super_admin" ||
      r === "admin" ||
      r === "curator" ||
      r === "content_manager",
  );
}

function canEditContent(roles: string[]): boolean {
  return roles.some((r) => r === "super_admin" || r === "admin" || r === "content_manager");
}

const STATUS_LABELS = {
  draft: "Черновик",
  published: "Опубликован",
  archived: "Архив",
} as const;

type Tab = "all" | "theory" | "tests";

export function CourseDetailPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const id = Number(courseId);
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [course, setCourse] = useState<CourseDetail | null>(null);
  const [moduleDetails, setModuleDetails] = useState<ModuleDetail[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newModuleOpen, setNewModuleOpen] = useState(false);
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [newModuleDescription, setNewModuleDescription] = useState("");
  const [tab, setTab] = useState<Tab>("all");

  const reload = useCallback(async () => {
    if (!Number.isFinite(id)) {
      setError("Некорректный курс");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const c = await getCourse(id);
      setCourse(c);
      setModuleDetails(null); // Invalidate; re-fetched on demand below
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setError("Курс не найден");
      else setError(err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Fetch all module details (with lessons) when we need flat-tabs view.
  useEffect(() => {
    if (tab === "all" || !course || moduleDetails !== null) return;
    void Promise.all(course.modules.map((m) => getModule(m.id)))
      .then(setModuleDetails)
      .catch(() => setModuleDetails([]));
  }, [tab, course, moduleDetails]);

  if (!user) return null;
  const canEdit = canEditContent(user.roles);

  async function handlePublish() {
    if (!course) return;
    if (!confirm(`Опубликовать «${course.title}»? Курс станет видим ученикам.`)) return;
    try {
      await publishCourse(course.id);
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  async function handleArchive() {
    if (!course) return;
    if (!confirm(`Перевести «${course.title}» в архив? Ученики потеряют доступ.`)) return;
    try {
      await archiveCourse(course.id);
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  async function handleDelete() {
    if (!course) return;
    if (!confirm(`Удалить курс «${course.title}»? Можно будет восстановить из архива.`)) return;
    try {
      await deleteCourse(course.id);
      navigate("/courses");
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  async function handleCreateModule(e: FormEvent) {
    e.preventDefault();
    if (!course) return;
    try {
      await createModule(course.id, {
        title: newModuleTitle.trim(),
        description: newModuleDescription.trim() || undefined,
      });
      setNewModuleTitle("");
      setNewModuleDescription("");
      setNewModuleOpen(false);
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  async function handleDeleteModule(m: ModuleListItem) {
    if (!confirm(`Удалить модуль «${m.title}»? Уроки останутся в библиотеке.`)) return;
    try {
      await deleteModule(m.id);
      await reload();
    } catch (err) {
      alert(err instanceof ApiError ? `Ошибка ${err.status}` : "Не удалось");
    }
  }

  if (loading && !course) {
    return <div className="mx-auto max-w-5xl px-4 py-12 text-center text-slate-400">Загрузка…</div>;
  }

  if (error || !course) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-12 text-center">
        <p className="text-slate-500">{error ?? "Курс не найден"}</p>
        <Link to="/courses" className="mt-4 inline-block text-sm text-brand-accent hover:underline">
          ← к списку курсов
        </Link>
      </div>
    );
  }

  async function handleCoverChange(newCoverId: string | null) {
    if (!course) return;
    await updateCourse(course.id, { cover_file_id: newCoverId });
    await reload();
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <Link to="/courses" className="text-sm text-ink-muted hover:text-brand">
        ← Все курсы
      </Link>

      {/* Hero: cover image (or placeholder) + title overlay style */}
      {course.cover_file_id ? (
        <div className="relative mt-3 overflow-hidden rounded-2xl ring-1 ring-line">
          <img
            src={fileUrl(course.cover_file_id)}
            alt={course.title}
            className="aspect-[21/9] w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/25 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <span
              className={`badge ${
                course.status === "published"
                  ? "badge-success"
                  : course.status === "draft"
                    ? "badge-warn"
                    : "badge-neutral"
              }`}
            >
              {STATUS_LABELS[course.status]}
            </span>
            <h1 className="mt-2 text-3xl font-bold leading-tight drop-shadow-sm">
              {course.title}
            </h1>
            {course.description && (
              <p className="mt-2 max-w-2xl text-sm text-white/90">{course.description}</p>
            )}
          </div>
        </div>
      ) : (
        <header className="mt-3">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-ink">{course.title}</h1>
                <span
                  className={`badge ${
                    course.status === "published"
                      ? "badge-success"
                      : course.status === "draft"
                        ? "badge-warn"
                        : "badge-neutral"
                  }`}
                >
                  {STATUS_LABELS[course.status]}
                </span>
              </div>
              <p className="mt-1 font-mono text-xs text-ink-muted">{course.slug}</p>
              {course.description && (
                <p className="mt-3 max-w-2xl text-sm text-ink">{course.description}</p>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Actions row */}
      {canEdit && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-xs text-ink-muted">{course.slug}</p>
          <div className="flex gap-2">
            {course.status === "draft" && (
              <button
                type="button"
                onClick={handlePublish}
                className="btn-primary bg-brand-success hover:bg-emerald-700"
              >
                Опубликовать
              </button>
            )}
            {course.status === "published" && (
              <button
                type="button"
                onClick={handleArchive}
                className="btn-secondary"
              >
                В архив
              </button>
            )}
            <button
              type="button"
              onClick={handleDelete}
              className="btn-danger"
            >
              Удалить
            </button>
          </div>
        </div>
      )}

      {/* Cover uploader (compact, shown when editing) */}
      {canEdit && (
        <section className="mt-6">
          <h2 className="mb-2 text-sm font-semibold text-ink-soft">Обложка</h2>
          <CoverUploader
            coverFileId={course.cover_file_id}
            onChange={handleCoverChange}
          />
        </section>
      )}

      <section className="mt-6">
        {/* Tabs */}
        <div className="mb-4 flex items-center gap-1 border-b border-line">
          {([
            { id: "all", label: "Все уроки" },
            { id: "theory", label: "Теория" },
            { id: "tests", label: "Тесты" },
          ] as { id: Tab; label: string }[]).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`border-b-2 px-3 py-2 text-sm font-medium transition ${
                tab === t.id
                  ? "border-brand text-brand"
                  : "border-transparent text-ink-muted hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab !== "all" && (
          <FlatLessonsView
            moduleDetails={moduleDetails}
            tab={tab}
            courseFinalQuizId={course.final_quiz_id}
          />
        )}

        {tab !== "all" ? null : (
          <>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-ink">Модули</h2>
          {canEdit && (
            <button
              type="button"
              onClick={() => setNewModuleOpen((s) => !s)}
              className="btn-secondary"
            >
              {newModuleOpen ? "Отмена" : "+ Добавить модуль"}
            </button>
          )}
        </div>

        {newModuleOpen && (
          <form
            onSubmit={handleCreateModule}
            className="mb-3 space-y-2 rounded-lg border border-slate-200 bg-white p-4"
          >
            <input
              type="text"
              required
              placeholder="Название модуля"
              value={newModuleTitle}
              onChange={(e) => setNewModuleTitle(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
              autoFocus
            />
            <textarea
              rows={2}
              placeholder="Описание (необязательно)"
              value={newModuleDescription}
              onChange={(e) => setNewModuleDescription(e.target.value)}
              className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                className="btn-primary"
              >
                Создать модуль
              </button>
            </div>
          </form>
        )}

        {course.modules.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
            В курсе пока нет модулей.
          </div>
        ) : (
          <ol className="space-y-2">
            {course.modules.map((m, idx) => (
              <li
                key={m.id}
                className="flex items-center justify-between rounded-lg bg-white px-4 py-3 shadow-sm"
              >
                <Link to={`/modules/${m.id}`} className="min-w-0 flex-1 group">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{idx + 1}.</span>
                    <span className="font-medium text-slate-900 group-hover:text-brand-accent">
                      {m.title}
                    </span>
                    {!m.is_required && (
                      <span className="badge badge-neutral">
                        Опц.
                      </span>
                    )}
                  </div>
                  {m.description && (
                    <p className="mt-0.5 truncate text-xs text-slate-500">{m.description}</p>
                  )}
                </Link>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleDeleteModule(m)}
                    className="ml-3 text-xs text-red-600 hover:underline"
                  >
                    Удалить
                  </button>
                )}
              </li>
            ))}
          </ol>
        )}
          </>
        )}
      </section>

      <AssignmentsPanel courseId={course.id} canManage={canManageAssignments(user.roles)} />
    </div>
  );
}

function FlatLessonsView({
  moduleDetails,
  tab,
  courseFinalQuizId,
}: {
  moduleDetails: ModuleDetail[] | null;
  tab: "theory" | "tests";
  courseFinalQuizId: number | null;
}) {
  if (moduleDetails === null) {
    return <div className="py-6 text-center text-sm text-ink-muted">Загружаем…</div>;
  }
  // Theory: text / video / mixed / checklist / file
  // Tests: quiz lesson_type
  const flat = moduleDetails.flatMap((m) =>
    m.lessons.map((l) => ({ module: m, lesson: l })),
  );
  const isTheory = (t: string) => ["text", "video", "mixed", "checklist", "file"].includes(t);
  const filtered =
    tab === "theory"
      ? flat.filter((x) => isTheory(x.lesson.lesson_type))
      : flat.filter((x) => x.lesson.lesson_type === "quiz");

  if (filtered.length === 0 && tab === "tests" && courseFinalQuizId === null) {
    return (
      <div className="rounded-lg border border-dashed border-line bg-white p-8 text-center text-sm text-ink-muted">
        В курсе пока нет тестов.
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-line bg-white p-8 text-center text-sm text-ink-muted">
        Пусто.
      </div>
    );
  }

  return (
    <ol className="space-y-2">
      {filtered.map(({ module, lesson }, idx) => (
        <li key={`${module.id}-${lesson.lesson_id}`}>
          <Link
            to={`/lessons/${lesson.lesson_id}`}
            className="card group flex items-center justify-between p-3 transition hover:-translate-y-0.5 hover:ring-brand/40 hover:shadow-md"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-xs text-ink-muted">{idx + 1}.</span>
                <span className="font-medium text-ink group-hover:text-brand">
                  {lesson.title}
                </span>
                <span className="badge badge-neutral">{lesson.lesson_type}</span>
              </div>
              <div className="mt-0.5 text-xs text-ink-muted">{module.title}</div>
            </div>
            {lesson.estimated_minutes && (
              <div className="ml-3 shrink-0 text-xs text-ink-muted">
                {lesson.estimated_minutes} мин
              </div>
            )}
          </Link>
        </li>
      ))}
    </ol>
  );
}
